let gptInitialized = false;

export default class GoogleAdvertisement {
  /**
   * @param {Object} options
   * @param {HTMLElement} options.container
   * @param {String} options.adUnitPath
   * @param {Boolean} [options.setTechnicalTarget=false]
   * @param {Array} options.sizes
   * @param {Array} [options.sizeMappingRules=[]]
   * @param {Number} [options.scrollVisibility=0] - Seconds to stay sticky
   */
  constructor({
    container,
    adUnitPath,
    setTechnicalTarget = false,
    sizes,
    sizeMappingRules = [],
    scrollVisibility = 0,
  }) {
    if (!container || !(container instanceof HTMLElement)) {
      throw new Error('GoogleAdvertisement: A valid container HTMLElement is required.');
    }
    if (!adUnitPath || typeof adUnitPath !== 'string') {
      throw new Error('GoogleAdvertisement: adUnitPath must be a non-empty string.');
    }
    if (!Array.isArray(sizes)) {
      throw new Error('GoogleAdvertisement: sizes must be an array.');
    }

    this.dom = {
      container,
      advertisement: container.closest('.c-advertisement-sticky'),
    };

    this.data = {
      sizes,
      sizeMappingRules,
      adUnitPath,
    };

    this.properties = {
      setTechnicalTarget,
      scrollVisibility,
      advertisementHeight: 0,
      advertisementPaddingY: 20,
    };

    this.customEvents = {
      adSlotRendered: 'adSlotRendered',
    };

    this.events = {
      onTransitionend: this.onTransitionend.bind(this),
      onScroll: this.onScroll.bind(this),
    };

    this.initAd();
  }

  /**
   * Initializes the advertisement slot
   */
  initAd() {
    window.googletag.cmd.push(() => {
      let slot = this.createSlot();

      // Apply size mapping if defined
      if (this.data.sizeMappingRules.length > 0) {
        slot = slot.defineSizeMapping(this.createSizeMapping().build());
      }

      // Add the slot to pubads service
      slot.addService(window.googletag.pubads());

      // Set technical targeting if needed
      window.googletag.pubads().setTargeting('Technical', [
        String(this.properties.setTechnicalTarget),
      ]);

      window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
        if (!event.isEmpty && event.slot === slot) {
          this.dom.container.closest('.c-advertisement')?.classList.add('is-rendered');

          // Dispatch a custom event
          window.dispatchEvent(
            new CustomEvent(this.customEvents.adSlotRendered, { detail: { slot: event.slot } }),
          );

          // Initialize sticky behavior if configured
          if (this.properties.scrollVisibility > 0) {
            this.initStickyAdvertisement();
          }
        }
      });
      window.googletag.pubads().addEventListener('slotOnload', (event) => {
        if (!event.isEmpty && event.slot === slot) {
          this.dom.container.closest('.c-advertisement')?.classList.add('is-loaded');
        }
      });
    });
  }

  /**
   * Creates a GPT slot
   * @returns {googletag.Slot}
   */
  createSlot() {
    return window.googletag.defineSlot(
      this.data.adUnitPath,
      this.data.sizes,
      this.dom.container.id,
    );
  }

  /**
   * Creates responsive size mapping
   * @returns {googletag.SizeMapping}
   */
  createSizeMapping() {
    const mapping = window.googletag.sizeMapping();
    this.data.sizeMappingRules.forEach(([viewportSize, adSizes]) => {
      mapping.addSize(viewportSize, adSizes);
    });
    return mapping;
  }

  /**
   * Initializes sticky ad behavior
   */
  initStickyAdvertisement() {
    this.properties.advertisementHeight = this.dom.container.offsetHeight + this.properties.advertisementPaddingY;

    this.dom.container.parentElement.style.height = `${this.properties.advertisementHeight}px`;
    document.body.style.paddingTop = `${this.properties.advertisementHeight}px`;

    this.dom.advertisement?.classList.remove('position-relative');
    this.dom.advertisement?.classList.add('position-fixed');

    window.addEventListener('scroll', this.events.onScroll, { once: true });
  }

  onScroll() {
    this.setAdvertisementCounter();
  }

  /**
   * Countdown to hide sticky ad
   */
  setAdvertisementCounter() {
    setTimeout(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      if (scrollTop > 0) {
        this.dom.advertisement?.classList.add('slide-up');
        this.dom.advertisement?.addEventListener('transitionend', this.events.onTransitionend, {
          once: true,
        });

        // Fallback if transitionend never fires
        setTimeout(this.events.onTransitionend, 1000);
      } else {
        this.onTransitionend();
      }
    }, this.properties.scrollVisibility);
  }

  onTransitionend() {
    document.body.style.paddingTop = '0px';
    this.dom.advertisement?.classList.remove('position-fixed', 'slide-up');
    this.dom.advertisement?.classList.add('position-relative');
    this.dom.advertisement.style.transform = 'translateY(0)';
  }

  /**
   * Destroy the ad (cleanup)
   */
  destroy() {
    document.body.style.paddingTop = '0px';
    if (this.dom.advertisement) {
      this.dom.advertisement.classList.remove('position-fixed', 'slide-up');
      this.dom.advertisement.classList.add('position-relative');
      this.dom.advertisement.style.transform = 'translateY(0)';
    }
    window.removeEventListener('scroll', this.events.onScroll);
    this.dom.advertisement?.removeEventListener('transitionend', this.events.onTransitionend);
  }

  static initializeGpt() {
    if (gptInitialized) {
      return;
    }

    window.googletag = window.googletag || { cmd: [] };

    window.googletag.cmd.push(() => {
      window.googletag.setConfig({
        collapseDiv: null,
      });

      /* window.googletag.pubads().addEventListener('slotRequested', (event) => {
        console.log('slotRequested', event.slot.getSlotElementId());
      });

      window.googletag.pubads().addEventListener('slotOnload', (event) => {
        console.log('slotOnload', event.slot.getSlotElementId());
      });

      window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
        console.log('slotRenderEnded', event.slot.getSlotElementId());
      });

      window.googletag.pubads().addEventListener('slotOnload', (event) => {
        console.log('slotOnload', event.slot.getSlotElementId());
      }); */

      // CALL ONCE, GLOBALLY
      window.googletag.enableServices();
    });

    gptInitialized = true;
  }
}
